import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Database, Plug, DownloadCloud, Archive, RotateCcw, Clock } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, StatCard, EmptyState, Spinner } from '../components/ui';
import { platformConnectionsApi, backupsApi, schedulesApi, restoresApi } from '../lib/api';
import type { PlatformConnection, BackupJob, Snapshot, BackupSchedule } from '../types/vault';

type BackupStatus = BackupJob['status'];

const STATUS_BADGE_TONE: Record<BackupStatus, 'success' | 'accent' | 'danger' | 'neutral' | 'warning'> = {
  completed: 'success',
  running: 'accent',
  failed: 'danger',
  cancelled: 'neutral',
  pending: 'warning',
};

export default function VaultDashboard() {
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [backupJobs, setBackupJobs] = useState<BackupJob[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [connectionsRes, backupsRes, snapshotsRes, schedulesRes] = await Promise.all([
        platformConnectionsApi.list(),
        backupsApi.list(),
        backupsApi.listSnapshots(),
        schedulesApi.list(),
      ]);

      if (connectionsRes.success && connectionsRes.data) {
        setConnections(connectionsRes.data.connections);
      }
      if (backupsRes.success && backupsRes.data) {
        setBackupJobs(backupsRes.data.jobs);
      }
      if (snapshotsRes.success && snapshotsRes.data) {
        setSnapshots(snapshotsRes.data.snapshots);
      }
      if (schedulesRes.success && schedulesRes.data) {
        setSchedules(schedulesRes.data.schedules);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <EmptyState
          icon={Database}
          title="Error Loading Dashboard"
          description={error}
          action={
            <Button variant="primary" icon={RotateCcw} onClick={loadDashboardData}>
              Retry
            </Button>
          }
        />
      </Layout>
    );
  }

  const recentJobs = backupJobs.slice(0, 10);

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Vault"
          title="Vault Dashboard"
          description="Overview of your backup connections, jobs, and schedules."
        />

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/vault/connections" className="block">
            <StatCard label="Connections" value={connections.length} icon={Plug} tone="accent" />
          </Link>
          <Link to="/vault/backups" className="block">
            <StatCard label="Recent Backups" value={backupJobs.length} icon={DownloadCloud} tone="cyan" />
          </Link>
          <Link to="/vault/snapshots" className="block">
            <StatCard label="Snapshots" value={snapshots.length} icon={Archive} tone="violet" />
          </Link>
          <Link to="/vault/schedules" className="block">
            <StatCard label="Schedules" value={schedules.length} icon={Clock} tone="amber" />
          </Link>
        </div>

        {/* Recent Backup Jobs */}
        <Card>
          <div className="p-6 border-b border-line">
            <div className="flex justify-between items-center">
              <h2 className="font-display text-lg font-bold text-ink">Recent Backup Jobs</h2>
              <Link
                to="/vault/backups"
                className="text-sm text-accent-ink hover:opacity-80 font-medium"
              >
                View all
              </Link>
            </div>
          </div>

          {recentJobs.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={DownloadCloud}
                title="No backup jobs yet"
                description="Start a backup from the Backups page to see jobs here."
                action={
                  <Link to="/vault/backups">
                    <Button variant="ghost" icon={DownloadCloud}>Go to Backups</Button>
                  </Link>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-2">
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Status</th>
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Platform</th>
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Connection</th>
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Items</th>
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Started</th>
                    <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.map((job) => (
                    <tr key={job.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                      <td className="px-6 py-3">
                        <Badge tone={STATUS_BADGE_TONE[job.status]}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-[13px] text-ink">
                        {job.platform || '-'}
                      </td>
                      <td className="px-6 py-3 text-[13px] text-ink-2">
                        {job.connectionName || job.connectionId}
                      </td>
                      <td className="px-6 py-3 text-[13px] text-ink-2 font-mono">
                        {job.processedItems}/{job.totalItems}
                        {job.failedItems > 0 && (
                          <span className="text-danger ml-1">({job.failedItems} failed)</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-[13px] text-ink-2 font-mono">
                        {job.startedAt
                          ? new Date(job.startedAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-6 py-3 text-[13px] text-ink-2 font-mono">
                        {job.completedAt
                          ? new Date(job.completedAt).toLocaleDateString()
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
